import '../styles.css';
import React, { useState, useRef, useCallback, useMemo, useEffect, } from 'react';
import { View, Tooltip } from '@smarterservices/smarter-ui';
import { usePlayerStore, getStore } from '../../../../stores/StoreContext';
import AnnotationPopover from './Popover';
import CONSTANTS from '../../../../config/constants';

// Helper to capitalize first letter
const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const RangeAnnotation = ({ annotation, row = 0, totalRows = 1, rowHeight = 8, verticalOffset = null, onMouseEnter, onMouseLeave }) => {
  const minWidth = "4px";

  const duration = usePlayerStore((state) => state.duration);
  const PlayerStore = getStore('playerStore');
  const ExamSessionStore = getStore('examSessionStore');
  const markerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isShowingTooltip, setIsShowingTooltip] = useState(false);
  const [top, setTop] = useState(0);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const elementId = `annotation_${annotation.sid}`;

  const handleAnnotationClick = useCallback(() => {
    if (
      annotation.start > CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
    ) {
      PlayerStore.jumpTo(
        annotation.start - CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
      );
    } else {
      PlayerStore.jumpTo(0);
    }
  }, [annotation.start, PlayerStore]);

  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      const rect = document.getElementById(elementId).getBoundingClientRect();
      setContextMenuPosition({ top: rect.bottom, left: rect.left });
      setShowContextMenu(true);
      ExamSessionStore.setManagingAnnotation({ ...annotation });
    },
    [annotation, ExamSessionStore]
  );

  // Memoize width calculation to prevent re-renders
  const width = useMemo(() => {
    let width = 0;

    // if the duration is null and there is a start and end date (this means it was very fast, set the min width)
    if (!annotation.duration && annotation?.range?.start && annotation?.range?.end) {
      width = minWidth;
      return width;
    }

    if (annotation?.duration) {
      width = `${(annotation.duration / duration) * 100}%`;
    } else {
      width = `${((duration - annotation.start) / duration) * 100}%`;
    }
    return width;
  }, [annotation.duration, annotation.start, duration, annotation.range]);

  const severity = PlayerStore.flagScoreToSeverity(annotation.score);
  const themeOverride = {
    [`background${capitalizeFirstLetter(severity.color)}`]: severity.backgroundColor
  };

  useEffect(() => {
    if (verticalOffset !== null) {
      setTop(verticalOffset);
    } else if (row === 0 && totalRows === 1) {
      setTop(rowHeight);
    } else {
      setTop(row * rowHeight);
    }
  }, [row, totalRows, rowHeight, verticalOffset]);

  return (
    <View
      elementRef={(el) => {
        markerRef.current = el;
      }}
      id={elementId}
      as="div"
      className="flag-range-marker"
      withVisualDebug={false}
      position="absolute"
      background={isHovered ? 'primary' : (annotation?.color ? annotation.color : severity?.color)}
      borderColor="secondary"
      borderWidth="small"
      borderRadius="small"
      padding="none"
      textAlign="center"
      width={width}
      minWidth={minWidth}
      height={`${rowHeight - 1}px`}
      onClick={handleAnnotationClick}
      themeOverride={isHovered ? { backgroundPrimary: '#000000' } : themeOverride}
      //onContextMenu={handleContextMenu}
      onMouseEnter={(e) => {
        setIsHovered(true);
        onMouseEnter?.(e, annotation);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        onMouseLeave?.(e);
      }}
      style={{
        left: `${(annotation.start / duration) * 100}%`, // Offset by start time
        top: `${top}px`, // Offset vertically based on row
        cursor: 'pointer',
      }}
    ></View>
  );
};

export default React.memo(RangeAnnotation);
